import {
  observable, action, computed, reaction,
} from 'mobx';
import Collection, {
  COLLECTION_TYPE_DIAMOND,
  COLLECTION_TYPE_JEWELRY,
  COLLECTION_TYPE_ROUGH,
} from '../entity/Collection';
import CollectionsAPI from '../api/CollectionsAPI';
import {TYPE_DIAMOND_COLORLESS} from '../../product/constants/productTypes';
import token from '@/user/services/token';
import Notifier from '@/common/services/Notifier';
import ProductFactory from '../../product/entity/ProductFactory';
import {appStore} from "@/common/stores/AppStore";
import { userStore } from '@/user/stores/UserStore';
import { sortCollectionsByDate } from '@/collection/helpers/sortCollectionsByDate';
import useStore from '@/common/hooks/useStore';

class CollectionsStore {
  /**
   * @type {Object<{ [key: number]: Collection[] }>}
   */
  @observable
  collectionsByType = {
    [COLLECTION_TYPE_DIAMOND]: null,
    [COLLECTION_TYPE_JEWELRY]: null,
    [COLLECTION_TYPE_ROUGH]: null,
  };

  /**
   * @type {Collection | null}
   */
  @observable
  activeCollection = null;

  collectionsPromises = {};

  /**
   * @param {AppStore} appStore
   * @param {UserStore} userStore
   */
  constructor(appStore, userStore) {
    this.appStore = appStore;
    this.collectionsAPI = new CollectionsAPI();

    this.initCollections();

    reaction(
      () => this.activeProductTypeContext,
      this.initCollections,
    );

    reaction(
      () => userStore?.user,
      (user) => {
        if (!user?.isAnonymous) {
          this.initCollections()
        }
      },
    );
  }

  @computed
  get activeProductTypeContext() {
    return this.appStore.productTypeContext || TYPE_DIAMOND_COLORLESS;
  }

  @computed
  get collections() {
    const cType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (!this.collectionsByType[cType]) {
      return null;
    }

    const filteredCollectionsByType = this.collectionsByType[cType].filter(c => c.type === cType);

    return sortCollectionsByDate(filteredCollectionsByType);
  }

  @action
  initCollections = () => {
    if (token.isAuthorized) {
      this.fetchCollections();
    }

    if (!this.collections || !this.collections.find(c => c === this.activeCollection)) {
      this.activeCollection = null;
    }
  };

  @action
  fetchCollections() {
    const collectionType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (this.collectionsPromises[collectionType]) {
      return;
    }

    this.collectionsPromises[collectionType] = this.collectionsAPI.fetchOwned(collectionType).then((res) => {
      if (!res.ok && res.content && res.content.message && res.status !== 404) {
        Notifier.error(res.content.message);
      }

      const collections = res.content ? res.content.map(d => new Collection(d, this.activeProductTypeContext)) : null;
      this.collectionsByType[collectionType] = collections && collections.length > 0 ? [...collections.filter(c => c.updatedAt !== null), ...collections.filter(c => c.updatedAt === null)] : [];
    });
  }

  /**
   * @param {string} title
   * @param {?Product} product
   * @return {Promise}
   */
  @action
  createNewCollection = (title, product = null) => {
    return this.collectionsAPI
      .create(this.activeProductTypeContext, title)
      .then(
        action((res) => {
          const collection = new Collection(res.data, this.activeProductTypeContext);
          if (product) {
            this.addProductToCollection(product, collection);
          }

          const collectionType = Collection.getTypeByProductType(this.activeProductTypeContext);
          this.collectionsByType[collectionType].unshift(collection);

          this.activeCollection = collection;
        }),
      )
      .catch(this.appStore.setError);
  };

  /**
   * @param {Product} product
   * @param {Collection} collection
   */
  @action
  addProductToCollection = (product, collection) => {
    if (!collection.products) {
      collection.products = [];
    }
    collection.products.push(product);

    return this.collectionsAPI.addToCollection(collection.id, [product.id]).then(() => {
      if (product.b2bSid) {
        Notifier.success(`${product.b2bSid} was added to collection "${collection.title}"`);
      }
    });
  };

  /**
   * @param {Product} product
   * @param {Collection} collection
   */
  @action
  removeProductFromCollection = (product, collection) => {
    collection.products = collection.products.filter(p => p.id !== product.id);

    return this.collectionsAPI.removeProductFromCollection(product, collection).then(() => {
      if (product.b2bSid) {
        Notifier.success(`${product.b2bSid} was removed from collection "${collection.title}"`);
      }
    });
  };

  /**
   * @param {Collection} c
   */
  @action
  toggleActive = (c) => {
    if (this.activeCollection === c) {
      this.activeCollection = null;
    } else {
      this.setActiveCollection(c);
    }
  };

  @action
  remove = (c) => {
    const { title } = c;

    if (this.activeCollection === c) {
      this.activeCollection = null;
    }

    const cType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (!this.collectionsByType[cType]) {
      return;
    }

    this.collectionsByType[cType] = this.collectionsByType[cType].filter(cItem => cItem.id !== c.id);

    this.collectionsAPI.remove(c.id).then(

      action((res) => {
        if (!res.ok && res.data && res.data.message && res.status !== 404) {
          this.collectionsByType[cType].push(c);
          Notifier.error(res.data.message);
          return;
        }

        Notifier.success(`Collection ${title} was removed`);
      }),
    );
  };

  /**
   * @param {Collection} c
   */
  @action
  setActiveCollection = (c) => {
    this.activeCollection = c;
    this.collectionsAPI.touchCollection(c);
  };

  /**
   * @param {Collection} collection
   * @param {[number]} pIds
   */
  @action
  wishAll = (collection, pIds) => {
    return this.collectionsAPI.addToCollection(collection.id, pIds).then(
      action((res) => {
        if (!res.ok && res.data && res.data.message && res.status !== 404) {
          Notifier.error(res.data.message);

          return res;
        }

        pIds.forEach((pid) => {
          const product = collection.products.find(p => p.id === pid);
          if (!product) {
            collection.products.push(ProductFactory.make(this.appStore.productTypeContext, { id: pid }));
          }
        });

        return res;
      }),
    );
  };

  /**
   * @param {Collection} collection
   */
  @action
  addCollection(collection) {
    const collectionType = Collection.getTypeByProductType(this.appStore.productTypeContext);

    if (!this.collectionsByType[collectionType]) {
      this.collectionsByType[collectionType] = [];
    }

    this.collectionsByType[collectionType].push(collection);
  }
}

export const collectionsStore = new CollectionsStore(appStore, userStore);
export default CollectionsStore;

export const useCollectionsStore = () => useStore(CollectionsStore);
